import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";

const approachPoints = [
  {
    icon: "ri-chat-smile-2-line",
    title: "Clear communication",
    description:
      "We focus on explaining services and options in plain terms so patients can ask questions and make informed decisions.",
  },
  {
    icon: "ri-hospital-line",
    title: "Convenient outpatient care",
    description:
      "Visits are planned in an outpatient setting, keeping the experience straightforward and easy to schedule around daily life.",
  },
  {
    icon: "ri-microscope-line",
    title: "Appropriate diagnostic support",
    description:
      "Diagnostic testing is used to support care, helping provide a clearer picture before discussing next steps.",
  },
  {
    icon: "ri-user-heart-line",
    title: "Individualized discussions",
    description:
      "Each patient's needs are different, so services are discussed on a one-to-one basis rather than through a fixed template.",
  },
];

const expectations = [
  "Ask about the services the clinic offers and whether they may be relevant to your situation.",
  "Ask about current availability and how to arrange a visit.",
  "Ask what information or documents may be helpful to bring.",
  "Ask any general questions about the clinic before you visit.",
];

export default function ApproachSection() {
  return (
    <section className="w-full bg-background-50 px-4 md:px-6 py-16 md:py-24">
      <div className="max-w-content mx-auto">
        <Reveal>
          <SectionHeading
            eyebrow="Our approach"
            title="Our Approach"
            description="Our approach is grounded in clear communication, convenient outpatient care, appropriate diagnostic support, and individualized discussions about each patient's needs."
          />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {approachPoints.map((point, index) => (
            <Reveal key={point.title} delay={index * 80}>
              <div className="h-full rounded-xl border border-background-200 bg-background-50 p-6">
                <span className="w-11 h-11 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className={`${point.icon} text-xl text-primary-700`} />
                </span>
                <h3 className="mt-5 font-heading text-base font-semibold text-foreground-950">
                  {point.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                  {point.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <Reveal className="lg:col-span-5">
            <div className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-8">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent-700">
                <span className="w-6 h-px bg-accent-400" />
                Before your visit
              </span>
              <h3 className="mt-4 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                What to Expect
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                Visitors are welcome to contact the clinic before scheduling. You can ask about
                services, availability, appointments, and any information that may be helpful
                before you visit.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <ul className="space-y-3">
              {expectations.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 px-5 py-4"
                >
                  <span className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-question-line text-accent-700" />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground-700">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-xl border border-primary-200 bg-primary-50 p-5 md:p-6">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center shrink-0">
                  <i className="ri-alert-line text-lg text-background-50" />
                </span>
                <p className="text-sm leading-relaxed text-foreground-800">
                  <strong className="font-semibold text-foreground-950">Disclaimer: </strong>
                  This website provides general informational content and is not a substitute for
                  professional medical advice, diagnosis, or treatment. For medical emergencies,
                  call 911 or contact emergency services.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}